import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { searchCities } from "@/lib/citySearch";

/**
 * CityPicker — searchable city autocomplete.
 * value: { city, state, country, lat, lng }
 * onChange(value)
 */
export default function CityPicker({ value, onChange, testidPrefix = "city" }) {
  const [q, setQ] = useState(value?.display || value?.city || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const tref = useRef();

  useEffect(() => {
    clearTimeout(tref.current);
    if (!q || q.length < 2) { setResults([]); return; }
    setBusy(true);
    tref.current = setTimeout(async () => {
      const ctrl = new AbortController();
      const r = await searchCities(q, { signal: ctrl.signal });
      setResults(r);
      setBusy(false);
    }, 250);
    return () => clearTimeout(tref.current);
  }, [q]);

  const pick = (r) => {
    onChange?.(r);
    setQ(r.display);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin size={16} strokeWidth={2.4} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/55 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Your city…"
          data-testid={`${testidPrefix}-input`}
          className="w-full border-[2.5px] border-ink rounded-2xl bg-paper pl-10 pr-10 py-3 text-base font-medium outline-none focus:bg-paper transition-colors"
        />
        {busy && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/55 animate-spin" />}
      </div>

      {open && results.length > 0 && (
        <div data-testid={`${testidPrefix}-results`} className="absolute z-30 left-0 right-0 mt-1 border-[2.5px] border-ink rounded-2xl bg-paper shadow-pop-sm max-h-72 overflow-y-auto">
          {results.map((r, i) => (
            <button
              type="button"
              key={`${r.city}-${r.lat}-${r.lng}-${i}`}
              onMouseDown={() => pick(r)}
              data-testid={`${testidPrefix}-result-${i}`}
              className="w-full text-left px-4 py-3 border-b border-ink/10 last:border-b-0 hover:bg-ink hover:text-paper transition-colors"
            >
              <div className="text-sm font-bold">{r.city}</div>
              <div className="text-[11px] uppercase tracking-widest text-ink/55 mt-0.5">
                {[r.state, r.country].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
